import { z } from 'zod';

// ============================================================================
// Base Schemas
// ============================================================================

export const EmailAddressSchema = z.object({
  email: z.string(),
  name: z.string().nullable().optional(),
});

export type EmailAddress = z.infer<typeof EmailAddressSchema>;

// ============================================================================
// Attachment Schema
// ============================================================================

export const AttachmentSchema = z.object({
  id: z.coerce.string(),
  filename: z.string(),
  size: z.number(),
  mimeType: z.string().nullable().optional(),
  contentType: z.string().nullable().optional(),
  contentId: z.string().nullable().optional(),
  partNumber: z.string().nullable().optional(),
  inline: z.boolean().optional(),
  downloaded: z.boolean().optional(),
  previewUrl: z.string().nullable().optional(),
  downloadUrl: z.string().nullable().optional(),
  validated: z.boolean().optional(),
  mediaClass: z.string().nullable().optional(),
});

export type Attachment = z.infer<typeof AttachmentSchema>;

// ============================================================================
// Auto Label Schema
// ============================================================================

export const AutoLabelSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.enum(['ai', 'sender']),
  source: z.enum(['ai', 'sender']),
  reason: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
});

export type AutoLabel = z.infer<typeof AutoLabelSchema>;

// ============================================================================
// Spam Report Schema
// ============================================================================

export const SpamSymbolDetailSchema = z.object({
  name: z.string().optional(),
  score: z.number().optional(),
  description: z.string().optional(),
  options: z.array(z.string()).optional(),
  metric_score: z.number().optional(),
}).passthrough();

export const SpamReportSchema = z.object({
  score: z.number().optional(),
  action: z.string().optional(),
  required_score: z.number().optional(),
  urls: z.array(z.string()).optional(),
  emails: z.array(z.string()).optional(),
  symbols: z.record(z.string(), SpamSymbolDetailSchema).optional(),
  milter: z.record(z.string(), z.unknown()).optional(),
  messages: z.record(z.string(), z.unknown()).optional(),
  thresholds: z.record(z.string(), z.number()).optional(),
  time_real: z.number().optional(),
  is_skipped: z.boolean().optional(),
}).passthrough();

export type SpamReport = z.infer<typeof SpamReportSchema>;

// ============================================================================
// Calendar Event Schema (embedded in emails)
// ============================================================================

export const CalendarEventSchema = z.object({
  id: z.string().nullable().optional(),
  uid: z.string().nullable().optional(),
  summary: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  startDate: z.string().nullable().optional(),
  startDateTime: z.string().nullable().optional(),
  startTimezone: z.string().nullable().optional(),
  endDate: z.string().nullable().optional(),
  endDateTime: z.string().nullable().optional(),
  endTimezone: z.string().nullable().optional(),
  allDay: z.boolean().nullable().optional(),
  organizer: z.object({
    email: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    displayName: z.string().nullable().optional(),
  }).nullable().optional(),
  attendees: z.array(z.object({
    email: z.string().nullable().optional(),
    name: z.string().nullable().optional(),
    displayName: z.string().nullable().optional(),
    responseStatus: z.string().nullable().optional(),
    partstat: z.string().nullable().optional(),
  })).nullable().optional(),
  status: z.string().nullable().optional(),
  method: z.string().nullable().optional(),
  responseStatus: z.string().nullable().optional(),
  respondedAt: z.string().nullable().optional(),
}).passthrough();

export type CalendarEvent = z.infer<typeof CalendarEventSchema>;

// ============================================================================
// AI Summary Schema
// ============================================================================

export const AiSummarySchema = z.object({
  summary: z.string().optional(),
  keyPoints: z.array(z.string()).optional(),
  actionItems: z.array(z.string()).optional(),
  sentiment: z.string().optional(),
  priority: z.string().optional(),
}).passthrough();

export type AiSummary = z.infer<typeof AiSummarySchema>;

// ============================================================================
// Email Schema
// ============================================================================

export const EmailSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  uid: z.string().nullable().optional(),
  folderId: z.string().nullable(),
  threadId: z.string().nullable().optional(),
  messageId: z.string().nullable().optional(),
  inReplyTo: z.string().nullable().optional(),
  references: z.string().nullable().optional(),
  subject: z.string(),
  from: EmailAddressSchema,
  to: z.array(EmailAddressSchema),
  cc: z.array(EmailAddressSchema).optional(),
  bcc: z.array(EmailAddressSchema).optional(),
  replyTo: z.array(EmailAddressSchema).optional(),
  date: z.string().nullable().transform((val) => (val ? new Date(val) : new Date())),
  read: z.boolean(),
  starred: z.boolean(),
  draft: z.boolean().optional(),
  body: z.string(),
  bodyHtml: z.string().nullable().optional(),
  snippet: z.string().nullable().optional(),
  attachments: z.array(AttachmentSchema).optional(),
  // Raw MIME headers are pass-through data whose shape is mailparser's to decide,
  // not ours: structured headers (`list` for List-Unsubscribe, address headers,
  // content-type params) arrive as nested objects since the toHeadersObject rework.
  // Validating them buys nothing and a shape we didn't anticipate would fail the
  // WHOLE message parse — which is exactly what bricked every List-Unsubscribe
  // message on 2026-08-22. Consumers coerce what they read.
  headers: z.record(z.string(), z.unknown()).optional(),
  flags: z.array(z.string()).optional(),
  keywords: z.array(z.string()).optional(),
  // Contact info
  approvedSender: z.boolean().optional(),
  contactApprovalState: z.enum(['pending', 'approved', 'blocked']).nullable().optional(),
  contactId: z.string().nullable().optional(),
  participantId: z.string().nullable().optional(),
  // Thread/sender counts
  messageCount: z.number().nullable().optional(),
  senderMailboxCount: z.number().nullable().optional(),
  // Spam info
  spamScore: z.number().nullable().optional(),
  spamRequiredScore: z.number().nullable().optional(),
  spamAction: z.string().nullable().optional(),
  spamReport: SpamReportSchema.nullable().optional(),
  spamScannedAt: z.string().nullable().optional(),
  // Training info
  trainedAsSpam: z.boolean().optional(),
  trainedAsGood: z.boolean().optional(),
  lastTrainedAt: z.string().nullable().optional(),
  lastTrainedLabel: z.enum(['spam', 'ham']).nullable().optional(),
  lastTrainedSource: z.string().nullable().optional(),
  // Calendar
  calendarEvent: CalendarEventSchema.nullable().optional(),
  // AI
  aiSummary: AiSummarySchema.nullable().optional(),
  aiQuickSummary: z.string().nullable().optional(),
  aiPriorityScore: z.number().nullable().optional(),
  // Survey
  isFlaggedSurvey: z.boolean().optional(),
  // Unsubscribe
  hasUnsubscribeLink: z.boolean().optional(),
  unsubscribeUrl: z.string().nullable().optional(),
  // Labels
  autoLabels: z.array(AutoLabelSchema).default([]),
  // OCR
  ocrText: z.string().nullable().optional(),
  ocrStatus: z.string().nullable().optional(),
  ocrMetadata: z.record(z.string(), z.unknown()).nullable().optional(),
  ocrProcessedAt: z.string().nullable().optional(),
});

export type Email = z.infer<typeof EmailSchema>;

export const EmailListSchema = z.array(EmailSchema);

// ============================================================================
// Thread Message Schema (extends Email with thread-specific fields)
// ============================================================================

export const ThreadMessageSchema = EmailSchema.extend({
  internalId: z.string().optional(),
  sequence: z.number().optional(),
  mailboxPaths: z.array(z.string()).optional(),
  isArchived: z.boolean().optional(),
  isTrashed: z.boolean().optional(),
});

export type ThreadMessage = z.infer<typeof ThreadMessageSchema>;

// ============================================================================
// Participant Schema
// ============================================================================

export const ParticipantSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable().optional(),
  avatarUrl: z.string().nullable().optional(),
  lastSeenAt: z.string().nullable().optional(),
  contactId: z.string().nullable().optional(),
});

export type Participant = z.infer<typeof ParticipantSchema>;

// ============================================================================
// Thread Schema
// ============================================================================

export const ThreadSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  folderId: z.string(),
  subject: z.string(),
  snippet: z.string().optional(),
  messageCount: z.number().optional(),
  unreadCount: z.number().optional(),
  lastMessageAt: z.string(),
  participants: z.array(ParticipantSchema),
  messages: z.array(ThreadMessageSchema),
});

export type Thread = z.infer<typeof ThreadSchema>;

// ============================================================================
// Folder/Mailbox Schema
// ============================================================================

const FolderSourceSchema = z.object({
  accountId: z.string(),
  folderId: z.string(),
});

export const FolderSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  name: z.string(),
  path: z.string(),
  role: z.string().nullable().optional(),
  unifiedRole: z.string().nullable().optional(),
  unreadCount: z.number(),
  totalCount: z.number().optional(),
  lastSyncedAt: z.string().nullable().optional(),
  notificationsEnabled: z.boolean().optional(),
  notificationsInherited: z.boolean().optional(),
  // Unified mailbox source folders
  sourceFolders: z.array(FolderSourceSchema).optional(),
  // UI enrichment (added by frontend)
  icon: z.string().optional(),
  systemFolder: z.boolean().optional(),
  count: z.number().optional(),
  hidden: z.boolean().optional(),
  visibility: z.enum(['always', 'more', 'never']).nullable().optional(),
});

export type Folder = z.infer<typeof FolderSchema>;

export const FolderListSchema = z.array(FolderSchema);

// ============================================================================
// Contact Schema
// ============================================================================

export const ContactSchema = z.object({
  id: z.string(),
  accountId: z.string(),
  participantId: z.string().nullable().optional(),
  email: z.string(),
  displayName: z.string().nullable().optional(),
  approvedState: z.enum(['pending', 'approved', 'blocked']),
  lastActivityAt: z.string().nullable().optional(),
  lastMessageAt: z.string().nullable().optional(),
  lastMessagePreview: z.object({
    messageId: z.string(),
    html: z.string().nullable().optional(),
    text: z.string().nullable().optional(),
    snippet: z.string().nullable().optional(),
    subject: z.string().nullable().optional(),
  }).nullable().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  totalMessages: z.number().optional(),
  unreadCount: z.number().optional(),
  hasUnread: z.boolean().optional(),
  score: z.number().optional(),
  sentCount: z.number().optional(),
  receivedCount: z.number().optional(),
  notificationsEnabled: z.boolean().default(true),
  aiReviewRecommendation: z.object({
    id: z.string().optional(),
    status: z.enum(['pending', 'running', 'complete', 'error']),
    decision: z.enum(['yes', 'no']).nullable().optional(),
    reason: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
    messageId: z.string().optional(),
  }).nullable().optional(),
  // Fields returned when blocking/approving
  movedEmailIds: z.array(z.string()).optional(),
  movedCount: z.number().optional(),
  targetFolderPath: z.string().nullable().optional(),
});

export type Contact = z.infer<typeof ContactSchema>;

export const ContactListSchema = z.array(ContactSchema);

// ============================================================================
// Account Schema
// ============================================================================

export const AccountSchema = z.object({
  id: z.string(),
  emailAddress: z.string(),
  displayName: z.string().nullable().optional(),
  provider: z.string().nullable().optional(),
  status: z.enum(['idle', 'syncing', 'error', 'disconnected', 'paused']),
  lastSyncedAt: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
  unreadCount: z.number().nullable().optional(),
  hasCredentials: z.boolean().optional(),
  color: z.string().nullable().optional(),
  defaultSignatureId: z.string().nullable().optional(),
});

export type Account = z.infer<typeof AccountSchema>;

export const AccountListSchema = z.array(AccountSchema);

// ============================================================================
// Sync Status Schemas
// ============================================================================

export const SyncMailboxStatusSchema = z.object({
  id: z.string(),
  name: z.string(),
  path: z.string(),
  role: z.string().optional(),
  accountId: z.string(),
  highestUid: z.number().nullable().optional(),
  expectedMessages: z.number().optional(),
  // Accept both naming conventions: running/in_progress and failed/error
  backfillStatus: z.enum(['pending', 'running', 'in_progress', 'complete', 'failed', 'error']).optional(),
  historicalCompletedAt: z.string().nullable().optional(),
  processedMessages: z.number().optional(),
  progress: z.number().nullable().optional(),
  lastDeltaAt: z.string().nullable().optional(),
});

export const AccountSyncStatusSchema = z.object({
  accountId: z.string(),
  status: z.enum(['idle', 'syncing', 'error', 'paused', 'disconnected']),
  progress: z.number().nullable().optional(),
  processedMessages: z.number().optional(),
  targetMessages: z.number().nullable().optional(),
  lastSyncedAt: z.string().nullable().optional(),
  errorMessage: z.string().nullable().optional(),
});

export const SyncJobSchema = z.object({
  id: z.string(),
  // Include all known job types from both backend variations
  jobType: z.enum(['historical_backfill', 'delta_sync', 'full_sync', 'manual_refresh', 'attachment_download']),
  state: z.enum(['queued', 'running', 'completed', 'failed', 'cancelled']),
  accountId: z.string(),
  mailboxId: z.string().nullable().optional(),
  progressCurrent: z.number().nullable().optional(),
  progressMax: z.number().nullable().optional(),
  error: z.string().nullable().optional(),
  scheduledAt: z.string().nullable().optional(),
  startedAt: z.string().nullable().optional(),
  finishedAt: z.string().nullable().optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
});

export const SyncStatusSchema = z.object({
  mailboxes: z.array(SyncMailboxStatusSchema),
  totals: z.object({
    targetMessages: z.number(),
    processedMessages: z.number(),
    actualProcessedMessages: z.number().optional(),
  }),
  overallProgress: z.number().nullable().optional(),
  accounts: z.array(AccountSyncStatusSchema).optional(),
  jobs: z.array(SyncJobSchema).optional(),
});

export type SyncStatus = z.infer<typeof SyncStatusSchema>;
export type AccountSyncStatus = z.infer<typeof AccountSyncStatusSchema>;
export type SyncMailboxStatus = z.infer<typeof SyncMailboxStatusSchema>;
export type SyncJob = z.infer<typeof SyncJobSchema>;

// ============================================================================
// Realtime Event Schemas
// ============================================================================

export const RealtimeEmailReceivedSchema = z.object({
  type: z.literal('email.received'),
  accountId: z.string(),
  email: EmailSchema,
});

export const RealtimeEmailUpdatedSchema = z.object({
  type: z.literal('email.updated'),
  accountId: z.string(),
  email: EmailSchema,
});

export const RealtimeEmailDeletedSchema = z.object({
  type: z.literal('email.deleted'),
  accountId: z.string(),
  emailId: z.string(),
  folderId: z.string(),
});

export const RealtimeEmailMovedSchema = z.object({
  type: z.literal('email.moved'),
  accountId: z.string(),
  emailId: z.string(),
  fromFolderId: z.string(),
  toFolderId: z.string(),
});

export const RealtimeMailboxCountsSchema = z.object({
  type: z.literal('mailbox.counts'),
  accountId: z.string(),
  mailboxes: z.array(z.object({
    id: z.string(),
    accountId: z.string(),
    name: z.string(),
    path: z.string(),
    role: z.string().nullable().optional(),
    unreadCount: z.number(),
    totalCount: z.number(),
  })),
});

export const RealtimeSyncProgressSchema = z.object({
  type: z.literal('mailbox.sync.progress'),
  accountId: z.string(),
  mailboxId: z.string(),
  processed: z.number(),
  total: z.number(),
  remaining: z.number().optional(),
  status: z.string().optional(),
});

export const RealtimeContactStateChangedSchema = z.object({
  type: z.literal('contact.stateChanged'),
  accountId: z.string(),
  contactId: z.string(),
  approvedState: z.enum(['pending', 'approved', 'blocked']),
  notificationsEnabled: z.boolean(),
});

export const RealtimeFolderPreferencesUpdatedSchema = z.object({
  type: z.literal('folder.preferences.updated'),
  accountId: z.string(),
});

export const RealtimeConnectedSchema = z.object({
  type: z.literal('connected'),
});

export const RealtimeSyncStatusSchema = z.object({
  type: z.literal('sync.status'),
  status: SyncStatusSchema,
});

export const RealtimeAttachmentDownloadedSchema = z.object({
  type: z.literal('attachment.downloaded'),
  accountId: z.string(),
  emailId: z.string(),
  attachmentId: z.string(),
  metadata: z.object({
    filename: z.string().nullable().optional(),
    mimeType: z.string().nullable().optional(),
    size: z.number().nullable().optional(),
  }).optional(),
});

export const RealtimeSyncJobUpdatedSchema = z.object({
  type: z.literal('sync.job.updated'),
  accountId: z.string(),
  job: SyncJobSchema,
});

// Sender alias merge: mergedContactIds were deleted and folded into contactId.
export const RealtimeContactMergedSchema = z.object({
  type: z.literal('contact.merged'),
  accountId: z.coerce.string(),
  contactId: z.coerce.string(),
  approvedState: z.enum(['approved', 'blocked', 'pending']),
  mergedContactIds: z.array(z.coerce.string()),
});

export const RealtimeContactReviewRecommendationSchema = z.object({
  type: z.literal('contact.reviewRecommendation'),
  accountId: z.string(),
  contactId: z.string(),
  recommendation: z.object({
    id: z.string().optional(),
    status: z.enum(['pending', 'running', 'complete', 'error']),
    decision: z.enum(['yes', 'no']).nullable().optional(),
    reason: z.string().nullable().optional(),
    updatedAt: z.string().nullable().optional(),
    messageId: z.string().optional(),
  }).nullable().optional(),
});

export const RealtimeMailboxListChangedSchema = z.object({
  type: z.literal('mailbox.list.changed'),
  accountId: z.string(),
});

export const RealtimeOperationFailedSchema = z.object({
  type: z.literal('operation.failed'),
  accountId: z.string(),
  operationType: z.string(),
  messageId: z.string().optional(),
  message: z.string(),
});

// --- Calendar realtime events -------------------------------------------------
// The rich calendar event body is owned by the calendar layer; we validate the
// envelope (discriminant + identifying fields) and pass the body through so a
// downstream shape change does not strip data. Only `id` is contractually
// required (see CalendarRealtimeEvent in types/email.ts).
export const RealtimeCalendarEventBodySchema = z.object({
  id: z.string(),
}).passthrough();

export const RealtimeCalendarEventUpsertSchema = z.object({
  type: z.literal(['calendar.event.created', 'calendar.event.updated']),
  accountId: z.string(),
  event: RealtimeCalendarEventBodySchema,
});

export const RealtimeCalendarEventDeletedSchema = z.object({
  type: z.literal('calendar.event.deleted'),
  accountId: z.string(),
  eventId: z.string(),
  calendarUid: z.string(),
});

export const RealtimeCalendarSyncStatusSchema = z.object({
  type: z.literal('calendar.sync.status'),
  accountId: z.string(),
  status: z.string(),
});

// --- Note realtime events -----------------------------------------------------
// Server always stamps scope:'note' and userId (see emitters.js). The note/tag
// bodies are passed through (validated by the notes layer downstream).
export const RealtimeNoteUpsertSchema = z.object({
  type: z.literal(['note.created', 'note.updated']),
  scope: z.literal('note').optional(),
  userId: z.string().optional(),
  note: z.object({}).passthrough().optional(),
});

export const RealtimeNoteDeletedSchema = z.object({
  type: z.literal('note.deleted'),
  scope: z.literal('note').optional(),
  userId: z.string().optional(),
  noteId: z.string(),
});

export const RealtimeNoteTagUpsertSchema = z.object({
  type: z.literal(['note.tag.created', 'note.tag.updated']),
  scope: z.literal('note').optional(),
  userId: z.string().optional(),
  tag: z.object({}).passthrough().optional(),
});

export const RealtimeNoteTagDeletedSchema = z.object({
  type: z.literal('note.tag.deleted'),
  scope: z.literal('note').optional(),
  userId: z.string().optional(),
  tagId: z.string(),
});

export const RealtimeEventSchema = z.discriminatedUnion('type', [
  RealtimeEmailReceivedSchema,
  RealtimeEmailUpdatedSchema,
  RealtimeEmailDeletedSchema,
  RealtimeEmailMovedSchema,
  RealtimeMailboxCountsSchema,
  RealtimeSyncProgressSchema,
  RealtimeContactStateChangedSchema,
  RealtimeFolderPreferencesUpdatedSchema,
  RealtimeMailboxListChangedSchema,
  RealtimeOperationFailedSchema,
  RealtimeConnectedSchema,
  RealtimeSyncStatusSchema,
  RealtimeAttachmentDownloadedSchema,
  RealtimeSyncJobUpdatedSchema,
  RealtimeContactReviewRecommendationSchema,
  RealtimeContactMergedSchema,
  RealtimeCalendarEventUpsertSchema,
  RealtimeCalendarEventDeletedSchema,
  RealtimeCalendarSyncStatusSchema,
  RealtimeNoteUpsertSchema,
  RealtimeNoteDeletedSchema,
  RealtimeNoteTagUpsertSchema,
  RealtimeNoteTagDeletedSchema,
]);

export type RealtimeEvent = z.infer<typeof RealtimeEventSchema>;

// ============================================================================
// Search Result Schemas
// ============================================================================

export const SearchHighlightSchema = z.object({
  field: z.string(),
  fragments: z.array(z.string()),
});

export const EmailSearchResultSchema = z.object({
  kind: z.literal('email'),
  accountId: z.string(),
  score: z.number().optional(),
  highlight: z.array(SearchHighlightSchema).optional(),
  email: EmailSchema,
});

export const ThreadSearchResultSchema = z.object({
  kind: z.literal('thread'),
  accountId: z.string(),
  score: z.number().optional(),
  highlight: z.array(SearchHighlightSchema).optional(),
  thread: ThreadSchema,
});

export const ContactSearchResultSchema = z.object({
  kind: z.literal('contact'),
  accountId: z.string(),
  score: z.number().optional(),
  highlight: z.array(SearchHighlightSchema).optional(),
  contact: ContactSchema,
});

export const SearchResultSchema = z.discriminatedUnion('kind', [
  EmailSearchResultSchema,
  ThreadSearchResultSchema,
  ContactSearchResultSchema,
]);

export type SearchResult = z.infer<typeof SearchResultSchema>;
export type SearchHighlight = z.infer<typeof SearchHighlightSchema>;

// ============================================================================
// Signature Schema
// ============================================================================

export const SignatureSchema = z.object({
  id: z.string(),
  name: z.string(),
  html: z.string(),
  text: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable().optional(),
});

export type Signature = z.infer<typeof SignatureSchema>;

// ============================================================================
// Validation Helper
// ============================================================================

export function parseWithSchema<T>(schema: z.ZodType<T>, data: unknown, context: string): T {
  const result = schema.safeParse(data);
  if (!result.success) {
    console.error(`[${context}] Schema validation failed:`, result.error.format());
    throw new Error(`[${context}] Invalid data: ${result.error.message}`);
  }
  return result.data;
}

export function parseArrayWithSchema<T>(
  schema: z.ZodType<T>,
  data: unknown,
  context: string
): T[] {
  if (!Array.isArray(data)) {
    console.error(`[${context}] Expected array but got:`, typeof data);
    throw new Error(`[${context}] Expected array but got: ${typeof data}`);
  }
  // Lossy on purpose: a single malformed row must not fail the whole list
  // (that blanks the inbox). Skip + log invalid items so they stay visible for
  // root-cause fixing while the rest of the list still renders.
  const results: T[] = [];
  let skipped = 0;
  data.forEach((item, index) => {
    const result = schema.safeParse(item);
    if (!result.success) {
      skipped += 1;
      console.error(`[${context}] Item ${index} validation failed (skipped):`, result.error.format());
      return;
    }
    results.push(result.data);
  });
  if (skipped > 0) {
    console.warn(`[${context}] Skipped ${skipped} of ${data.length} invalid item(s)`);
  }
  return results;
}
